import {Injectable, signal} from '@angular/core';

/**
 * Holds the actual {@link File} objects picked in a runtime form, keyed by leaf field key.
 *
 * A reactive `FormControl` can only carry a serialisable value, so the file input stores the *file name* — which
 * is all the validators and the review page need, but is useless for uploading. Before #258 that name was the
 * only trace of the pick: {@link FieldRendererComponent} never kept the `File`, so the first-fill screen had
 * nothing to send and every attachment was silently dropped. This service is the side-channel that keeps the
 * bytes alive from "picked" to "submitted".
 *
 * Provided by the screen that submits (e.g. `FormsCompletionComponent`); the field-renderer injects it
 * **optionally**, mirroring {@link MaskedFieldsState}, so a builder preview — where nothing is ever uploaded —
 * simply has no provider and the renderer stays inert.
 */
@Injectable()
export class SelectedFilesState {
  private readonly files = signal(new Map<string, File>());

  /** Records (or replaces) the file picked for a field. A field carries at most one attachment. */
  set(fieldKey: string, file: File): void {
    const next = new Map(this.files());
    next.set(fieldKey, file);
    this.files.set(next);
  }

  /** Forgets a field's pick (the employee cleared the input). */
  clear(fieldKey: string): void {
    if (!this.files().has(fieldKey)) return;
    const next = new Map(this.files());
    next.delete(fieldKey);
    this.files.set(next);
  }

  /** Drops every pick — call after a successful submit so the next form starts clean. */
  reset(): void {
    this.files.set(new Map());
  }

  /** The file picked for a field, or undefined. */
  get(fieldKey: string): File | undefined {
    return this.files().get(fieldKey);
  }

  /** True when at least one file is waiting to be sent (drives JSON vs. multipart on submit). */
  get any(): boolean {
    return this.files().size > 0;
  }

  /** Every pick as `fieldKey → File`, for building the multipart body. */
  snapshot(): Record<string, File> {
    return Object.fromEntries(this.files());
  }
}
